import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getHoldings, refreshAndWait } from '../../data/portfolio';
import { createBucket, getStockBenchmarks, getStocks, setAllocations } from '../../data/stocks';
import PortfolioTabs from '../portfolio/PortfolioTabs';
import PortfolioHeader from '../portfolio/PortfolioHeader';
import PositionsTable from '../portfolio/PositionsTable';
import NavChart from '../portfolio/NavChart';
import BenchmarkChart from '../portfolio/BenchmarkChart';
import ViewToggle from '../portfolio/ViewToggle';
import SectionSummary from '../portfolio/SectionSummary';
import BucketAllocations from '../portfolio/BucketAllocations';
import useThemes from '../portfolio/useThemes';
import useSummaryInputs from '../portfolio/useSummaryInputs';
import { FadeIn, Skeleton } from '../portfolio/Loading';
import { netDepositsIn, positionsInView, sumNavSeries, viewChange } from '../portfolio/accounts';
import { partsIn, summaryParts } from '../portfolio/sections';
import { bucketPart, bucketPositions, bucketReturns } from '../portfolio/buckets';
import { DEFAULT_TIMEFRAME, TIMEFRAMES } from '../portfolio/timeframes';
import { useT } from '../../i18n';

// Stock Investments: the Fidelity account, split into buckets.
//
// The summary and chart describe the whole account; each bucket below has its
// own cost, value and P/L, over All only — a bucket is a split of today's
// holdings and has no history of its own.

const LOAD_STATE = { LOADING: 'loading', READY: 'ready', ERROR: 'error' };
const DEFAULT_BUCKET = 'discretionary';

export default function StockInvestments() {
    const { t, fmt } = useT();
    const [params, setParams] = useSearchParams();
    const [state, setState] = useState(LOAD_STATE.LOADING);
    const [data, setData] = useState(null);
    const [stocks, setStocks] = useState({ buckets: [], allocations: [], defaultBucket: DEFAULT_BUCKET });
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [windowKey, setWindowKey] = useState('all');
    // The chart's own period: the summary's 1Y is the statement period, the
    // chart's a rolling twelve months, and one control for both would put
    // different dates against each other.
    const [timeframe, setTimeframe] = useState(DEFAULT_TIMEFRAME);
    const [adding, setAdding] = useState(null);
    // Value, or return against the S&P 500 and the NASDAQ. The comparison is
    // loaded with the page: every bucket's chart is built from it.
    const [chartMode, setChartMode] = useState('value');
    const [benchmarks, setBenchmarks] = useState(null);
    const themes = useThemes();
    const { inputs, setInput } = useSummaryInputs();

    const loadStocks = useCallback(async () => {
        const result = await getStocks();
        setStocks({
            buckets: result?.buckets ?? [],
            allocations: result?.allocations ?? [],
            defaultBucket: result?.defaultBucket ?? DEFAULT_BUCKET,
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        // Supplementary: a failure leaves the charts saying so, not the page.
        getStockBenchmarks()
            .then((result) => { if (!cancelled) setBenchmarks(result ?? { series: [] }); })
            .catch(() => { if (!cancelled) setBenchmarks({ series: [] }); });
        Promise.all([getHoldings(), loadStocks().catch(() => setNotice('stocks.errors.load'))])
            .then(([holdings]) => {
                if (cancelled) return;
                setData(holdings);
                setState(LOAD_STATE.READY);
            })
            .catch((err) => {
                if (cancelled) return;
                setError(err);
                setState(LOAD_STATE.ERROR);
            });
        return () => { cancelled = true; };
    }, [loadStocks]);

    const refresh = async () => {
        setRefreshing(true);
        setNotice(null);
        try {
            setData(await refreshAndWait(getHoldings, { previousAsOf: data?.asOf }));
        } catch (err) {
            setNotice('stocks.errors.refresh');
        } finally {
            setRefreshing(false);
        }
    };

    const accounts = useMemo(() => (data?.accounts ?? []).filter((a) => a.source === 'fidelity'), [data]);
    const positions = useMemo(() => positionsInView(data?.positions ?? [], accounts), [data, accounts]);
    const parts = useMemo(() => partsIn(summaryParts(accounts, { inputs }), 'stocks'), [accounts, inputs]);
    const navHistory = useMemo(() => sumNavSeries(accounts.map((a) => a.navHistory)), [accounts]);
    const change = useMemo(() => viewChange(accounts, timeframe), [accounts, timeframe]);
    const netDeposits = useMemo(() => netDepositsIn(accounts, timeframe), [accounts, timeframe]);
    const total = accounts.reduce((s, a) => s + Number(a.totalValue ?? 0), 0);

    const buckets = stocks.buckets.length > 0
        ? stocks.buckets
        : [{ key: DEFAULT_BUCKET, name: t('stocks.discretionary'), position: 0 }];
    const bucket = buckets.some((b) => b.key === params.get('bucket')) ? params.get('bucket') : buckets[0].key;
    const bucketRows = useMemo(
        () => bucketPositions(positions, stocks.allocations, bucket, stocks.defaultBucket),
        [positions, stocks, bucket],
    );
    const bucketName = buckets.find((b) => b.key === bucket)?.name;
    const bucketBench = useMemo(() => {
        if (!benchmarks) return null;
        const line = bucketReturns(bucketRows, benchmarks.symbols ?? {});
        return {
            series: [
                ...(line.length >= 2 ? [{ key: 'bucket', points: line }] : []),
                ...(benchmarks.series ?? []).filter((s) => s.key === 'spx' || s.key === 'ndx'),
            ],
        };
    }, [benchmarks, bucketRows]);

    const saveSplit = async (symbol, list) => {
        try {
            await setAllocations(symbol, list);
            await loadStocks();
            return true;
        } catch {
            return false;
        }
    };

    const addBucket = async () => {
        const name = (adding ?? '').trim();
        if (!name) { setAdding(null); return; }
        try {
            const created = await createBucket(name);
            await loadStocks();
            setAdding(null);
            if (created?.key) setParams({ bucket: created.key });
        } catch {
            setNotice('stocks.errors.bucket');
        }
    };

    return (
        <section className="holdings__sec">
            <div className="wrapper">
                <PortfolioHeader
                    title={t('stocks.title')}
                    status={data?.asOf && t('common.asOf', { when: fmt.dateTime(data.asOf) })}
                    onRefresh={refresh}
                    refreshing={refreshing}
                />

                <PortfolioTabs />

                {state === LOAD_STATE.LOADING && <Skeleton rows={10} />}

                {state === LOAD_STATE.ERROR && (
                    <p className="holdings__error" role="alert">{error?.message || t('holdings.error')}</p>
                )}

                {state === LOAD_STATE.READY && (
                    <>
                        {notice && <p className="holdings__error" role="alert">{t(notice)}</p>}

                        {accounts.length > 0 && (
                            <div className="holdings__total">
                                <span className="holdings__total_value">{fmt.number(total)}</span>
                                <span className="holdings__total_currency">{data.baseCurrency}</span>
                            </div>
                        )}

                        <SectionSummary
                            parts={parts}
                            windowKey={windowKey}
                            onWindow={setWindowKey}
                            inputs={inputs}
                            onInput={setInput}
                        />

                        {navHistory.length > 1 && (
                            <>
                                <div className="holdings__timeframe holdings__chartbar">
                                    <ViewToggle
                                        ariaLabel={t('timeframe.aria')}
                                        options={TIMEFRAMES.map((f) => [f.key, t(f.labelKey)])}
                                        value={timeframe}
                                        onChange={setTimeframe}
                                    />
                                    <ViewToggle
                                        ariaLabel={t('benchmarks.modeAria')}
                                        options={[['value', t('benchmarks.value')], ['compare', t('benchmarks.compare')]]}
                                        value={chartMode}
                                        onChange={setChartMode}
                                    />
                                </div>
                                {chartMode === 'value' ? (
                                    <NavChart
                                        navHistory={navHistory}
                                        currency={data.baseCurrency}
                                        timeframe={timeframe}
                                        netDeposits={netDeposits}
                                        windowChange={change}
                                    />
                                ) : (
                                    benchmarks ? <BenchmarkChart data={benchmarks} timeframe={timeframe} /> : <Skeleton rows={4} />
                                )}
                            </>
                        )}

                        <nav className="itabs" aria-label={t('stocks.bucketsAria')}>
                            {buckets.map((b) => (
                                <button
                                    key={b.key}
                                    type="button"
                                    className={`itabs__tab${bucket === b.key ? ' is-active' : ''}`}
                                    onClick={() => setParams(b.key === buckets[0].key ? {} : { bucket: b.key })}
                                >
                                    {b.name}
                                </button>
                            ))}
                            {adding === null ? (
                                <button type="button" className="itabs__tab bucket__add" onClick={() => setAdding('')}>
                                    {t('stocks.addBucket')}
                                </button>
                            ) : (
                                <span className="bucket__new">
                                    <input
                                        className="private__input"
                                        value={adding}
                                        onChange={(e) => setAdding(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') addBucket(); if (e.key === 'Escape') setAdding(null); }}
                                        placeholder={t('stocks.bucketName')}
                                        aria-label={t('stocks.bucketName')}
                                        maxLength={60}
                                        autoFocus
                                    />
                                    <button type="button" className="notes__link" onClick={addBucket}>{t('common.save')}</button>
                                    <button type="button" className="notes__link" onClick={() => setAdding(null)}>{t('common.cancel')}</button>
                                </span>
                            )}
                        </nav>

                        <FadeIn viewKey={bucket}>
                            <SectionSummary parts={[bucketPart(bucketRows, bucket, bucketName)]} windowKey="all" />
                            {/* Today's shares of the bucket, over the chart's period. */}
                            {bucketBench && bucketRows.length > 0 && (
                                <BenchmarkChart data={bucketBench} timeframe={timeframe} names={{ bucket: bucketName }} />
                            )}
                            {bucketRows.length > 0 ? (
                                <PositionsTable positions={bucketRows} baseCurrency={data.baseCurrency} themes={themes} />
                            ) : (
                                <div className="holdings__empty"><p className="desc">{t('stocks.emptyBucket')}</p></div>
                            )}
                        </FadeIn>

                        <BucketAllocations
                            positions={positions}
                            allocations={stocks.allocations}
                            buckets={buckets}
                            defaultBucket={stocks.defaultBucket}
                            onSave={saveSplit}
                        />
                    </>
                )}
            </div>
        </section>
    );
}
